import { ReactElement } from 'react';
import { blue, charcoal, mediumGrey, orange, red } from '@/ui/styles';
import { UIIcon, UISpacer, UIText, UITooltip } from '@/ui';
import { useCommonTranslation, useFormatDate } from '../hooks';

const HEIGHT = 10;
const RADIUS = 2;
const LINE_WIDTH = 1;

type BatteryProps = {
    battery: { level: number; isCharging: boolean; timestamp: Date } | null;
    withLevel?: boolean;
};
export function Battery({
    battery,
    withLevel = true
}: BatteryProps): ReactElement {
    const { t } = useCommonTranslation();
    const { formatSmartShortTimeAndDate } = useFormatDate();

    function sanitiseBatteryLevel(batteryLevel: number): number {
        return Math.min(Math.max(Math.floor(batteryLevel), 0), 100);
    }

    const fill = battery ? sanitiseBatteryLevel(battery.level) / 100 : 1;
    const fillColour = battery
        ? battery.level > 20
            ? blue
            : battery.level > 10
              ? orange
              : red
        : mediumGrey;
    const isCharging = battery ? battery.isCharging : false;
    const isKnown = battery ? true : false;
    const batteryPercent = battery
        ? t('components.battery.level', {
              value: sanitiseBatteryLevel(battery.level)
          })
        : t('components.battery.unknown');

    let tooltipText = t('components.battery.no_data');
    if (battery) {
        const values = {
            value: sanitiseBatteryLevel(battery.level),
            time: formatSmartShortTimeAndDate(new Date(battery.timestamp))
        };
        tooltipText = battery.isCharging
            ? t('components.battery.tooltip_charging', values)
            : t('components.battery.tooltip', values);
    }

    return (
        <UITooltip text={tooltipText}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    opacity: isKnown ? 1 : 0.3
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        position: 'relative',
                        height: HEIGHT,
                        width: HEIGHT * 2,
                        border: `1px solid ${mediumGrey}`,
                        borderRadius: RADIUS,
                        flexDirection: 'row'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flex: fill,
                            margin: LINE_WIDTH,
                            borderRadius: RADIUS / 2,
                            backgroundColor: fillColour
                        }}
                    />
                    {isCharging && (
                        <div
                            style={{
                                display: 'flex',
                                position: 'absolute',
                                top: -10,
                                right: 0,
                                bottom: -10,
                                left: 0
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <UIIcon name="zap" size={15} color={charcoal} />
                            </div>
                        </div>
                    )}
                </div>
                <div
                    style={{
                        display: 'flex',
                        height: HEIGHT / 3,
                        width: LINE_WIDTH,
                        marginLeft: 1,
                        backgroundColor: mediumGrey
                    }}
                />
                <UISpacer multiplier={0.5} />
                {withLevel && (
                    <UIText variant={'basic'}>{batteryPercent}</UIText>
                )}
            </div>
        </UITooltip>
    );
}
